import queryString from 'query-string';

import { clearLocalStorage } from '../localstorage';

function appRedirect(from) {
  window.location.href = from || '/app';
}

/**
 * Default ttl of 7 days for session keys.
 * Note that we also auto-extend keys while
 * the session is active, so keys expire after
 * 7 days of inactivity.
 */
const TTL_7_DAYS = 7 * 24 * 60 * 60;

/**
 *
 * Session
 *
 * This slice of the redux store is intended to be the single
 * place where authentication information is set/retrieved.
 * This allows for the rmi to be easily instantiated, without
 * :wever storing the rmi in local storage, it also isolates
 * knowlege of the email/password, though email may be moved to the account store.
 */

/**
 * ##############################################
 * # ACTION CONSTANTS
 * ##############################################
 */
export const CLEAR_ERROR = 'SESSION::CLEAR_ERROR';
export const END_SESSION_SENT = 'SESSION::END_SESSION_SENT';
// NOTE: see intercom.js for non-imported definition of this same constant.
export const END_SESSION_SUCCESS = 'SESSION::END_SESSION_SUCCESS';
export const END_SESSION_FAIL = 'SESSION::END_SESSION_FAIL';
export const RMI_AUTH_SET = 'SESSION::RMI_AUTH_SET';
export const SET_CREDENTIAL_DATA = 'SESSION::SET_CREDENTIAL_DATA';
export const SIGN_IN_REQUEST_SENT = 'SESSION::SIGN_IN_REQUEST_SENT';
export const SIGN_IN_REQUEST_SUCCESS = 'SESSION::SIGN_IN_REQUEST_SUCCESS';
export const SIGN_IN_REQUEST_FAIL = 'SESSION::SIGN_IN_REQUEST_FAIL';

/**
 * ##############################################
 * # INITIAL STATE - STATE SIGNATURE
 * ##############################################
 */
const initialState = {
  email: '',
  error: undefined,
  rmiAuthSet: false,
  signedIn: false,
  signInRequestOut: false,
  signOutRequestOut: false,
};

/**
 * ##############################################
 * # REDUCER
 * ##############################################
 */

export default (state = initialState, action) => {
  switch (action.type) {
    case CLEAR_ERROR:
      return { ...state, error: undefined };
    case END_SESSION_SENT:
      return { ...state, error: undefined, signOutRequestOut: true };
    case END_SESSION_SUCCESS:
      return { ...initialState };
    case END_SESSION_FAIL:
      return { ...state, signOutRequestOut: false, error: action.error };
    case RMI_AUTH_SET:
      return { ...state, rmiAuthSet: true };
    case SET_CREDENTIAL_DATA:
      if (action.email !== undefined) {
        return { ...state, email: action.email };
      }
      break;
    case SIGN_IN_REQUEST_SENT:
      return { ...state, signInRequestOut: true, error: undefined };
    case SIGN_IN_REQUEST_SUCCESS:
      return {
        ...state,
        signedIn: true,
        signInRequestOut: false,
        error: undefined,
        temporaryKey: action.temporaryKey,
        rmiAuthSet: true,
      };
    case SIGN_IN_REQUEST_FAIL:
      return { ...state, signInRequestOut: false, error: action.error };
    default:
      return state;
  }
  return state;
};

/**
 * ##############################################
 * # ACTIONS
 * ##############################################
 *
 * NOTES:
 * 1. In a not very redux-y way, most of the actions in session have a side effect that manages the
 *    authentication state of the RMI
 * 2. To make the behavior of #1 less unbearable, session actions should be the ONLY place in the
 *    app that rmi auth is managed.
 * 3. sessionStore.rmiAuthSet is generally to be trusted, but could be out of sync during small
 *     parts of the authentication cycle, for example, between when sendSignInRequest is called, and
 *     when the request resolves, the rmi is authenticated (ish), but rmiAuthSet is false.
 *     Also, when loading from local storage, there will be a time that rmiAuthSet could be 'true',
 *     even though the rmi hasn't been instantiated yet.
 */
export const clearError = () => ({
  type: CLEAR_ERROR,
});

const endSessionHelper = ({
  from = '',
  redirect = true,
  reload = false,
} = {}) => (dispatch, getState) => {
  const { rmi, router } = getState();
  rmi.setAuth({});
  dispatch({ type: END_SESSION_SUCCESS });
  clearLocalStorage();
  if (reload) {
    window.location.reload();
  } else {
    const params = {};
    if (router.location.search) {
      const { e } = queryString.parse(router.location.search);
      params.e = e;
    }
    if (from && from.length) {
      params.from = from;
    }
    if (redirect) {
      window.location.href = `/auth/signin?${queryString.stringify(params)}`;
    }
  }
};

export const endSession = ({ reload = false, from = '' } = {}) => (dispatch, getState) => {
  const { rmi } = getState();
  const tempKey = getState().session.temporaryKey;
  dispatch({ type: END_SESSION_SENT });
  if (tempKey && tempKey.id) {
    rmi.keys.delete({ id: tempKey.id })
      .then(() => {
        dispatch(endSessionHelper({ reload, from }));
      })
      .catch((error) => {
        if (error.statusCode === 401) {
          dispatch(endSessionHelper({ from }));
        } else {
          dispatch({ type: END_SESSION_FAIL, error });
        }
      });
  } else {
    dispatch(endSessionHelper());
  }
};

const sendSignInRequestHelperEmail = (rmi, email, password, config) => {
  rmi.setAuth({ email, password });

  const ttl = config.ttl || TTL_7_DAYS;
  return rmi.keys.post({ ttl });
};

/*
 * Signing in using a secret is considered a dev feature,
 * and carries with it a few caveats.
 * 1. The email used should match the owner of the key, this isn't checked
 * 2. The `password` has to match secret= + the secret, a failed request
 * will give the same feedback as a failed password signin request.
 * 3. The dev must use a password that has a valid expiration date.
 */
const sendSignInRequestHelperSecret = (rmi, secret) => {
  rmi.setAuth({ secret });

  return rmi.keys.get({ secret });
};

/* Secret Regex
 * Used for both testing if the password starts with secret= and then 24 chars,
 * and for exposing the secret in the first matched group.
 */
const SecretRegex = /^secret=(.{24})/;

export const sendSignInRequest = (config = {}) => function dispatchSignIn(dispatch, getState) {
  const { email } = getState().session;
  const { password } = config;
  const { rmi } = getState();

  let rq;
  if (password.match(SecretRegex)) {
    const secret = password.match(SecretRegex)[1];
    rq = sendSignInRequestHelperSecret(rmi, secret);
  } else {
    rq = sendSignInRequestHelperEmail(rmi, email, password, config);
  }
  dispatch({ type: SIGN_IN_REQUEST_SENT });

  rq.then((temporaryKey) => {
    const key = { ...temporaryKey };
    if (key.api_key) {
      key.secret = key.api_key;
      delete key.api_key;
    }
    rmi.setAuth({ key });
    dispatch({ type: SIGN_IN_REQUEST_SUCCESS, temporaryKey: key });

    // Side Effect: Redirect the user to app
    const from = config.from || '';
    appRedirect(from);
  });

  rq.catch((error) => {
    dispatch({ type: SIGN_IN_REQUEST_FAIL, error });
  });

  return rq;
};

export const setCredentialData = (prop, value) => {
  const action = {
    type: SET_CREDENTIAL_DATA,
  };
  action[prop] = value;
  return action;
};

export const setRmiAuth = () => (dispatch, getState) => {
  const key = getState().session.temporaryKey;
  if (key && key.expires < new Date().toISOString()) {
    dispatch(endSessionHelper({ redirect: false }));
  } else {
    const { rmi } = getState();
    rmi.setAuth({ key });
    dispatch({ type: RMI_AUTH_SET });
  }
};
